// @givmo/passkeys — the organization door's step (AS-WEB30): identifier-first, in the door's own card
// (frames W2–W5, WF1–WF5): the work email, then the company's own sign-in page in a new window, then
// back, signed in. Its failures are the backend's own refusals, worded for the web. Signed out, it never
// names the company: the routing answer is uniform for accounts.
//
// ⚠ THE WINDOW OPENS ON THE PRESS, BLANK, AND IS SENT ON ONCE THE BACKEND ANSWERS. A window opened
// after an `await` has lost the press's user activation, and Safari (and Firefox past its short grace)
// block it as an unrequested popup — so the press opens `about:blank` synchronously (same-origin, so
// this page may then point it at the employer's page), and only a press whose window the browser
// refused falls back to the same tab. Google and Microsoft sign-ins do the same (Firebase's popup).
//
// ⚠ "THE WINDOW CLOSED" IS A GUESS, AND A LATE ANSWER STILL COUNTS. An identity provider's page with a
// strict Cross-Origin-Opener-Policy severs the window from this page: from here it then reads as
// closed while the person is still signing in there. So a closed window waits a moment, then says it
// didn't finish (WF2) — and an answer that arrives later still signs the person in, for as long as the
// attempt it belongs to is the latest one (`organization-sso-return.ts` carries it back either way).

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Button, ErrorMessage, TextField, cn, testIdProp } from '@givmo/design-system';
import { AuthError, createPkcePair } from '@givmo/auth';
import { useEscapeHatchEntry } from './escape-hatch-entry';
import { ORGANIZATION_SSO_WORDS as W } from './organization-sso-copy';
import {
  createOrganizationSsoTransport,
  organizationSsoFailureOf,
  organizationSsoFailureOfCode,
  type OrganizationSsoFailure,
} from './organization-sso-transport';
import { organizationSsoCallbackUrl } from './organization-sso-door';
import {
  listenForOrganizationSsoResult,
  saveOrganizationSsoAttempt,
  takeOrganizationSsoAttempt,
  type OrganizationSsoResult,
} from './organization-sso-return';
import { useStepTitleFocus } from './step-focus';

const PREFIX = 'organization-sso';
/** The window's name: one company sign-in window per tab, reused by a second press. */
const WINDOW_NAME = 'givmo-organization-sso';
const WINDOW_FEATURES = 'popup=yes,width=520,height=680';
/** How long a closed window waits for an answer already on its way before saying it didn't finish. */
const CLOSED_GRACE_MS = 2500;
const CLOSED_POLL_MS = 500;

const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:opacity-60';
/** Board's director footer looks (`apps/charity` `director-arrival.tsx`). */
const DIRECTOR_LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline motion-reduce:transition-none';
const DIRECTOR_QUIET_LINK =
  'font-sans text-s13 font-w400 tracking-givmo text-text-secondary outline-none transition-colors hover:text-text-strong focus-visible:underline motion-reduce:transition-none';

const FAILURE_LINE: Record<OrganizationSsoFailure, string> = {
  noSso: W.noSso,
  didNotFinish: W.didNotFinish,
  notLinked: W.notLinked,
  notHere: W.notHere,
  throttled: W.throttled,
};

type Stage = 'enter' | 'waiting' | 'signingIn';

/** The latest attempt this page started: its verifier, and its window while one is open. */
interface PendingAttempt {
  codeVerifier: string;
  window: Window | null;
}

export interface OrganizationSsoFlowProps<T> {
  /** The door's own heading look. */
  headingClass: string;
  /**
   * The console's sign-in with the handoff (its auth context's `signInWithOrganizationSso`): the
   * exchange, the custom-token sign-in and whatever the console runs after a sign-in. Resolves once
   * signed in, with whatever the console needs to land the person; rejects with the exchange's
   * refusal or an `AuthError`.
   */
  signIn: (params: { handoffCode: string; codeVerifier: string }) => Promise<T>;
  /** Signed in through the window: the host lands the person (where `returnTo` names). */
  onSignedIn: (result: T) => void;
  /** Where to land, kept with the attempt for the same-tab return (a sanitized same-origin path). */
  returnTo: string;
  /** Back to the door (the console's "Back to sign in", Board's "Back to all options"). */
  onBackToSignIn: () => void;
  /**
   * Where `onBackToSignIn` goes, as a sanitized same-origin path, kept with a same-tab attempt so a
   * return that could not finish reopens the step with the same way back (cut 1007e's review, F5).
   */
  backTo?: string;
  /** The way back in, from the step's foot where a sign-in lock exists here (as both apps do). */
  onLostAccess: () => void;
  /** The console's card, or Board's director register (its title and prompt are the shell's). */
  register?: 'console' | 'director';
  /** The address and refusal a same-tab return carried back to its callback page. */
  initialEmail?: string;
  initialFailure?: OrganizationSsoFailure | null;
  /** This page's callback URI; the console's own `/sso/callback` by default. */
  callbackUrl?: string;
}

/** A refusal of the sign-in itself, read as the door's state. */
function failureOfSignIn(thrown: unknown): OrganizationSsoFailure {
  // A Firebase failure of the custom-token sign-in: nothing the person can fix but trying again.
  if (thrown instanceof AuthError) return 'didNotFinish';
  return organizationSsoFailureOf(thrown);
}

export function OrganizationSsoFlow<T>({
  headingClass,
  signIn,
  onSignedIn,
  returnTo,
  onBackToSignIn,
  backTo,
  onLostAccess,
  register = 'console',
  initialEmail = '',
  initialFailure = null,
  callbackUrl = organizationSsoCallbackUrl(),
}: OrganizationSsoFlowProps<T>) {
  const [stage, setStage] = useState<Stage>('enter');
  const [email, setEmail] = useState(initialEmail);
  const [failure, setFailure] = useState<OrganizationSsoFailure | null>(initialFailure);
  const [busy, setBusy] = useState(false);
  const pending = useRef<PendingAttempt | null>(null);
  const title = useStepTitleFocus(stage);
  const escapeShown = useEscapeHatchEntry();
  const trimmed = email.trim();
  const director = register === 'director';

  // The answer the callback page carries back. Accepted only for the latest attempt this page
  // started, so a stale window cannot finish a sign-in the person has moved on from — and only
  // ONCE: the attempt is claimed here, before anything is awaited, so no second answer (or a
  // second copy of this one) can start another exchange of a single-use handoff while this one is
  // in flight (cut 1007e's review, F2; the listener drops the copy too, `organization-sso-return.ts`).
  const onResult = useCallback(
    async (result: OrganizationSsoResult) => {
      const attempt = pending.current;
      if (attempt === null) return;
      pending.current = null;
      if (result.handoffCode === null) {
        setStage('enter');
        setFailure(organizationSsoFailureOfCode(result.error));
        return;
      }
      setFailure(null);
      setStage('signingIn');
      try {
        const signedIn = await signIn({
          handoffCode: result.handoffCode,
          codeVerifier: attempt.codeVerifier,
        });
        onSignedIn(signedIn);
      } catch (thrown) {
        setStage('enter');
        setFailure(failureOfSignIn(thrown));
      }
    },
    [signIn, onSignedIn],
  );

  // One subscription for the page's life, always calling the latest handler.
  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);
  useEffect(() => listenForOrganizationSsoResult((result) => void onResultRef.current(result)), []);

  // While its window is open: a window that closed with no answer didn't finish (WF2) — after a
  // grace for an answer already on its way, and still finishable by a late one (the header).
  useEffect(() => {
    if (stage !== 'waiting') return undefined;
    let grace: ReturnType<typeof setTimeout> | undefined;
    const poll = setInterval(() => {
      const open = pending.current?.window;
      if (open === null || open === undefined || !open.closed) return;
      clearInterval(poll);
      grace = setTimeout(() => {
        if (pending.current === null) return;
        pending.current.window = null;
        setStage('enter');
        setFailure('didNotFinish');
      }, CLOSED_GRACE_MS);
    }, CLOSED_POLL_MS);
    return () => {
      clearInterval(poll);
      if (grace !== undefined) clearTimeout(grace);
    };
  }, [stage]);

  const start = useCallback(
    async (opened: Window | null) => {
      setBusy(true);
      setFailure(null);
      try {
        const { verifier, challenge } = await createPkcePair();
        const answer = await createOrganizationSsoTransport().route({
          email: trimmed,
          codeChallenge: challenge,
          redirectUri: callbackUrl,
        });
        if (!answer.ssoAvailable || answer.authorizeUrl === null) {
          opened?.close();
          setFailure('noSso');
          return;
        }
        if (opened !== null && opened.closed) {
          // The person closed the window before it was sent on: they changed their mind.
          return;
        }
        if (opened === null) {
          // The browser refused the window: this tab goes, and the callback page finishes here.
          if (
            !saveOrganizationSsoAttempt({
              codeVerifier: verifier,
              email: trimmed,
              returnTo,
              register,
              back: backTo ?? null,
            })
          ) {
            setFailure('didNotFinish');
            return;
          }
          globalThis.location.assign(answer.authorizeUrl);
          return;
        }
        pending.current = { codeVerifier: verifier, window: opened };
        opened.location.href = answer.authorizeUrl;
        setStage('waiting');
      } catch (thrown) {
        opened?.close();
        setFailure(organizationSsoFailureOf(thrown));
      } finally {
        setBusy(false);
      }
    },
    [trimmed, callbackUrl, returnTo, register, backTo],
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (busy || trimmed.length === 0) return;
    // A same-tab attempt this tab left unfinished must not ride into the new window: Chrome copies a
    // tab's sessionStorage into a window it opens on `about:blank`, and the callback page there
    // would spend this attempt's answer with the old verifier.
    takeOrganizationSsoAttempt();
    // On the press, before any await (the header): `null` means the browser refused it.
    let opened: Window | null = null;
    try {
      opened = globalThis.open('about:blank', WINDOW_NAME, WINDOW_FEATURES) ?? null;
    } catch {
      opened = null;
    }
    void start(opened);
  };

  const cancel = () => {
    const attempt = pending.current;
    pending.current = null;
    try {
      attempt?.window?.close();
    } catch {
      // A window severed from this page cannot be closed from here; the person closes it.
    }
    setStage('enter');
  };

  const heading = (
    <h1 ref={title} tabIndex={-1} className={cn(headingClass, 'text-balance outline-none')}>
      {W.title}
    </h1>
  );

  if (stage !== 'enter') {
    return (
      <div className="flex flex-col gap-20" {...testIdProp(`${PREFIX}-${stage}`)}>
        <div className="flex flex-col gap-4">
          {!director && heading}
          <p className={LINE} role="status">
            {stage === 'waiting' ? W.waiting : W.signingIn}
          </p>
        </div>
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          loading
          testId={`${PREFIX}-pending`}
        >
          {W.continue}
        </Button>
        {stage === 'waiting' && (
          <button
            type="button"
            className={LINK}
            onClick={cancel}
            {...testIdProp(`${PREFIX}-cancel`)}
          >
            {W.cancel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-20" {...testIdProp(`${PREFIX}-enter`)}>
      {!director && (
        <div className="flex flex-col gap-4">
          {heading}
          <p className={LINE}>{W.prompt}</p>
        </div>
      )}
      <form className="flex flex-col gap-16" noValidate onSubmit={onSubmit}>
        <div className="flex flex-col gap-8">
          <TextField
            label={W.emailLabel}
            type="email"
            autoComplete="email"
            value={email}
            error={failure === 'noSso'}
            onChange={(event) => {
              setEmail(event.target.value);
              setFailure(null);
            }}
            testId={`${PREFIX}-email`}
          />
          {failure === 'notLinked' ? (
            // Not a failure to retry: an explanation with two exits, so the notice look (WF3). The
            // second exit is the "Lost access" link, so the line names it only while the link shows.
            <p
              role="alert"
              className="mt-4 rounded-lg border border-warning-100 bg-warning-50 p-12 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-warning-700"
              {...testIdProp(`${PREFIX}-error`)}
            >
              {escapeShown ? FAILURE_LINE.notLinked : W.notLinkedNoEscape}
            </p>
          ) : failure !== null ? (
            <ErrorMessage testId={`${PREFIX}-error`}>{FAILURE_LINE[failure]}</ErrorMessage>
          ) : null}
        </div>
        <Button
          type="submit"
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          loading={busy}
          disabled={trimmed.length === 0}
          testId={`${PREFIX}-continue`}
        >
          {W.continue}
        </Button>
      </form>
      {director ? (
        // Board's director card footer: a rule, then the links centred, the way back quieter.
        <div className="flex flex-col items-center gap-8 border-t border-border-subtle pt-20 text-center">
          {escapeShown && (
            <button
              type="button"
              className={DIRECTOR_LINK}
              onClick={onLostAccess}
              {...testIdProp(`${PREFIX}-lost-access`)}
            >
              {W.lostAccess}
            </button>
          )}
          <button
            type="button"
            className={DIRECTOR_QUIET_LINK}
            onClick={onBackToSignIn}
            {...testIdProp(`${PREFIX}-back`)}
          >
            {W.backToAllOptions}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {escapeShown && (
            <button
              type="button"
              className={LINK}
              onClick={onLostAccess}
              {...testIdProp(`${PREFIX}-lost-access`)}
            >
              {W.lostAccess}
            </button>
          )}
          <button
            type="button"
            className={LINK}
            onClick={onBackToSignIn}
            {...testIdProp(`${PREFIX}-back`)}
          >
            {W.backToSignIn}
          </button>
        </div>
      )}
    </div>
  );
}
